/**
 * Speicher als Kreisdiagramm mit Prozenten – fuer Cloud (Dateien) und
 * Lead Sheets (Einstellungen → Speicher). Nur Anzeige; Aendern geht dort,
 * wo es hingehoert (Teilen eines Projekts, Mitglieder eines Orbits).
 *
 * Farben: share-c0 … share-c5 (styles/share.css), „frei“ als Spur.
 */
import { h } from './dom.ts';

const NS = 'http://www.w3.org/2000/svg';

export interface DonutPart {
  label: string;
  bytes: number;
  /** Zusatz rechts, z. B. „dein Anteil 60 %“. */
  note?: string;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1).replace('.', ',')} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2).replace('.', ',')} GB`;
}

/** Prozent mit einer Nachkommastelle unter 10 %, sonst ganz; nie „0 %“ fuer etwas Belegtes. */
export function percent(part: number, total: number): string {
  if (total <= 0 || part <= 0) return '0 %';
  const p = (part / total) * 100;
  if (p < 0.1) return '< 0,1 %';
  return `${(p < 10 ? p.toFixed(1) : Math.round(p).toString()).replace('.', ',')} %`;
}

export function storageDonut(opts: { parts: DonutPart[]; total: number; title?: string; empty?: string }): HTMLElement {
  const used = opts.parts.reduce((s, p) => s + Math.max(0, p.bytes), 0);
  const total = Math.max(opts.total, used, 1);
  const r = 50;
  const c = 2 * Math.PI * r;
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 120 120');
  svg.setAttribute('class', 'donut__svg');
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', `${percent(used, total)} belegt`);
  const track = document.createElementNS(NS, 'circle');
  for (const [k, v] of Object.entries({ cx: '60', cy: '60', r: String(r), class: 'donut__track' })) track.setAttribute(k, v);
  svg.append(track);
  let offset = 0;
  opts.parts.forEach((p, i) => {
    const share = p.bytes / total;
    if (share <= 0) return;
    const len = Math.max(share * c - (share < 0.99 ? 1.6 : 0), 1.2);
    const arc = document.createElementNS(NS, 'circle');
    for (const [k, v] of Object.entries({ cx: '60', cy: '60', r: String(r), class: `donut__arc share-c${i % 6}`, transform: 'rotate(-90 60 60)' })) arc.setAttribute(k, v);
    arc.setAttribute('stroke-dasharray', `${len} ${c}`);
    arc.setAttribute('stroke-dashoffset', String(-offset));
    arc.style.setProperty('--len', String(len));
    arc.style.setProperty('--delay', `${80 + i * 110}ms`);
    svg.append(arc);
    offset += share * c;
  });
  const free = Math.max(0, total - used);
  return h(
    'div',
    { class: 'donut' },
    h(
      'div',
      { class: 'donut__ring' },
      svg as unknown as HTMLElement,
      h('div', { class: 'donut__center' }, h('strong', { text: percent(used, total) }), h('span', { text: 'belegt' })),
    ),
    h(
      'div',
      { class: 'donut__legend' },
      opts.title ? h('p', { class: 'donut__title', text: opts.title }) : null,
      ...(opts.parts.length === 0 && opts.empty ? [h('p', { class: 'hint', text: opts.empty })] : []),
      ...opts.parts.map((p, i) =>
        h(
          'div',
          { class: 'donut__row' },
          h('span', { class: `share__dot share-c${i % 6}` }),
          h('span', { class: 'donut__name', text: p.label }),
          h('span', { class: 'donut__pct', text: percent(p.bytes, total) }),
          h('span', { class: 'donut__val', text: p.note ? `${formatBytes(p.bytes)} · ${p.note}` : formatBytes(p.bytes) }),
        ),
      ),
      h('div', { class: 'donut__row donut__row--free' }, h('span', { class: 'share__dot donut__dot-free' }), h('span', { class: 'donut__name', text: 'Frei' }), h('span', { class: 'donut__pct', text: percent(free, total) }), h('span', { class: 'donut__val', text: `${formatBytes(free)} von ${formatBytes(total)}` })),
    ),
  );
}
